import React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { MarkdownWrapper } from '../../../../components/WorkbookV2/MarkdownWrapper';
import { Checkbox } from '../../../../components/⚛️atoms/Checkbox';

const ToolItemsList = ({ items, handleDragEnd, handleEditClick, handleDeleteItem }) => {
  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="items-list">
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className="flex flex-col gap-3"
          >
            {items && items.length > 0 ? (
              items.map((toolItem, index) => (
                <Draggable
                  key={toolItem._id || toolItem.priority || index}
                  draggableId={String(toolItem._id || toolItem.priority || index)}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={`p-4 border border-gray-300 rounded-lg ${
                        snapshot.isDragging ? 'shadow-lg' : ''
                      }`}
                    >
                      <div className="flex gap-4">
                        {/* Preview as it will appear to users */}
                        <div className="flex-1">
                          {/* Content Type */}
                          {toolItem.type === 'content' && (
                            <div className="flex w-full items-center gap-2">
                              <div className="max-w-none">
                                <MarkdownWrapper>{toolItem.title}</MarkdownWrapper>
                              </div>
                            </div>
                          )}

                          {/* Checkbox Type */}
                          {toolItem.type === 'checkbox' && (
                            <div className="flex w-full items-center gap-2">
                              <div className="flex flex-col">
                                <MarkdownWrapper>{toolItem.question || toolItem.title}</MarkdownWrapper>
                                <ul className="ml-4">
                                  {Array.isArray(toolItem.options) && toolItem.options.map((option, idx) => {
                                    const optionText = typeof option === 'string' ? option : option.text || '';
                                    const optionSubtype = typeof option === 'string' ? '' : option.subtype || '';
                                    const displayLabel = optionSubtype
                                      ? `${optionText} (${optionSubtype})`
                                      : optionText;
                                    return (
                                      <li key={idx} className="my-2 flex items-center gap-3">
                                        <Checkbox
                                          label={displayLabel}
                                          checked={false}
                                          onChange={() => {}}
                                        />
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </div>
                          )}

                          {/* Radio Type */}
                          {toolItem.type === 'radio' && (
                            <div className="flex w-full items-center gap-2">
                              <div className="flex flex-col">
                                <MarkdownWrapper>{toolItem.question || toolItem.title}</MarkdownWrapper>
                                <ul className="mb-4 ml-8">
                                  {Array.isArray(toolItem.options) && toolItem.options.map((option, idx) => {
                                    const optionText = typeof option === 'string' ? option : option.text || '';
                                    const optionSubtype = typeof option === 'string' ? '' : option.subtype || '';
                                    return (
                                      <li key={idx} className="my-2 flex items-center gap-3">
                                        <label className="flex items-center gap-2">
                                          <input
                                            type="radio"
                                            name={`radio-preview-${index}`}
                                            className="w-6 h-8 border-2 border-pdteal accent-pdteal"
                                          />
                                          <span className="font-normal">
                                            {optionText}
                                            {optionSubtype && (
                                              <span className="text-xs text-gray-500 ml-2">({optionSubtype})</span>
                                            )}
                                          </span>
                                        </label>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </div>
                          )}

                          {/* Input Type */}
                          {toolItem.type === 'input' && (
                            <div className="flex w-full items-center gap-2">
                              <div className="flex w-full flex-col gap-2">
                                <MarkdownWrapper>{toolItem.question || toolItem.title}</MarkdownWrapper>
                                <input
                                  type="text"
                                  placeholder={toolItem.placeholder || 'Type your answer here...'}
                                  className="border-2 border-gray-300 rounded px-3 py-2 w-full focus:border-pdteal focus:outline-none"
                                  disabled
                                />
                              </div>
                            </div>
                          )}

                          {/* Textarea Type */}
                          {toolItem.type === 'textarea' && (
                            <div className="flex w-full items-center gap-2">
                              <div className="flex w-full flex-col gap-2">
                                <MarkdownWrapper>{toolItem.question || toolItem.title}</MarkdownWrapper>
                                <textarea
                                  placeholder={toolItem.placeholder || 'Type your answer here...'}
                                  className="border-2 border-gray-300 rounded px-3 py-2 w-full focus:border-pdteal focus:outline-none resize-none"
                                  rows={10}
                                  disabled
                                />
                              </div>
                            </div>
                          )}

                          {/* Rating Type */}
                          {toolItem.type === 'rating' && (
                            <div className="flex w-full items-center gap-2">
                              <div className="flex w-full flex-col gap-2">
                                <MarkdownWrapper>{toolItem.question || toolItem.title}</MarkdownWrapper>
                                <div className="relative flex w-3/4 flex-row gap-2 pt-10">
                                  {Array.from({ length: (toolItem.maxRating || 10) - (toolItem.minRating || 0) + 1 }, (_, i) => (
                                    <i
                                      key={i}
                                      className="peer size-12 text-center rounded-lg font-bold border-4 border-white ring-1 ring-gray-200"
                                    >
                                      {i + (toolItem.minRating || 0)}
                                    </i>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 items-center">
                          <div className="hover:bg-gray-200 p-2 rounded-xl transition-colors">
                            <PencilIcon className="text-pdteal size-8 cursor-pointer" onClick={() => handleEditClick(toolItem)}/>
                          </div>

                          {handleDeleteItem && (
                            <div className="hover:bg-gray-200 p-2 rounded-xl transition-colors">
                              <TrashIcon className="text-pdred size-8 cursor-pointer" onClick={() => handleDeleteItem(toolItem._id)}/>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No items yet. Click "Add Item" to create one.
              </div>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default ToolItemsList;
